import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type {
  CloudIAM,
  CloudIAMCreate,
  CloudIAMUpdate,
  CloudIAMList,
  CloudIAMValidateResponse,
} from './types'

export const iamApi = {
  /** Unpaginated list for dropdowns/selectors. Returns all IAM entries matching filters. */
  listAll: (params?: {
    cloud_provider?: string
    iam_subtype?: string
    status?: string
    validation_status?: string
    /** Comma-separated creator user ids */
    created_by?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<CloudIAMList>(`/iam/list-all${query}`, { signal })
  },

  list: (params?: {
    search?: string
    cloud_provider?: string
    iam_subtype?: string
    status?: string
    validation_status?: string
    /** Comma-separated creator user ids */
    created_by?: string
    page?: number
    page_size?: number
    signal?: AbortSignal
    tenantId?: string
  }) => {
    const { signal, tenantId, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<CloudIAMList>(`/iam${query}`, { signal, tenantId })
  },

  get: (id: string, options?: { signal?: AbortSignal }) =>
    request<CloudIAM>(`/iam/${id}`, { signal: options?.signal }),

  create: (data: CloudIAMCreate, options?: { signal?: AbortSignal }) =>
    request<CloudIAM>('/iam', {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  update: (id: string, data: CloudIAMUpdate, options?: { signal?: AbortSignal }) =>
    request<CloudIAM>(`/iam/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  delete: (id: string, options?: { signal?: AbortSignal }) =>
    request<void>(`/iam/${id}`, {
      method: 'DELETE',
      signal: options?.signal,
    }),

  validate: (id: string, options?: { signal?: AbortSignal }) => {
    let refreshToken: string | null = null
    try {
      refreshToken = localStorage.getItem('tagp-refresh-token')
    } catch {
      // Continue without refresh token
    }
    return request<CloudIAMValidateResponse>(`/iam/${id}/validate`, {
      method: 'POST',
      headers: {
        ...(refreshToken && { 'X-Azure-Refresh-Token': refreshToken }),
      },
      signal: options?.signal,
    })
  },

  msgraphAuthorize: (
    iamId: string,
    options?: { signal?: AbortSignal; isSystemNotificationSender?: boolean },
  ) =>
    request<{ auth_url: string; state: string; redirect_uri: string }>('/iam/msgraph/authorize', {
      method: 'POST',
      body: JSON.stringify({
        iam_id: iamId,
        is_system_notification_sender: options?.isSystemNotificationSender ?? false,
      }),
      signal: options?.signal,
    }),

  msgraphRedirectUri: (options?: { signal?: AbortSignal }) =>
    request<{ redirect_uri: string }>('/iam/msgraph/redirect-uri', {
      signal: options?.signal,
    }),

  /** Delegated OAuth for Azure Service Principal IAM used by connectors (OBO / stored refresh). */
  azureConnectorAuthorize: (
    iamId: string,
    options?: {
      connectorId?: string | null
      /** Sent as scopes_override — use when the IAM form has unsaved scope text. */
      scopesOverride?: string | null
      signal?: AbortSignal
    },
  ) =>
    request<{ auth_url: string; state: string; redirect_uri: string }>(
      '/iam/azure-connector/authorize',
      {
        method: 'POST',
        body: JSON.stringify({
          iam_id: iamId,
          ...(options?.connectorId ? { connector_id: options.connectorId } : {}),
          ...(options?.scopesOverride?.trim()
            ? { scopes_override: options.scopesOverride.trim() }
            : {}),
        }),
        signal: options?.signal,
      },
    ),

  azureConnectorRedirectUri: (options?: { signal?: AbortSignal }) =>
    request<{ redirect_uri: string }>('/iam/azure-connector/redirect-uri', {
      signal: options?.signal,
    }),

  gmailAuthorize: (iamId: string, options?: { signal?: AbortSignal }) =>
    request<{ auth_url: string; state: string; redirect_uri: string }>('/iam/gmail/authorize', {
      method: 'POST',
      body: JSON.stringify({ iam_id: iamId }),
      signal: options?.signal,
    }),

  gmailRedirectUri: (options?: { signal?: AbortSignal }) =>
    request<{ redirect_uri: string }>('/iam/gmail/redirect-uri', {
      signal: options?.signal,
    }),

  youtubeAuthorize: (iamId: string, options?: { signal?: AbortSignal }) =>
    request<{ auth_url: string; state: string; redirect_uri: string }>('/iam/youtube/authorize', {
      method: 'POST',
      body: JSON.stringify({ iam_id: iamId }),
      signal: options?.signal,
    }),

  youtubeRedirectUri: (options?: { signal?: AbortSignal }) =>
    request<{ redirect_uri: string }>('/iam/youtube/redirect-uri', {
      signal: options?.signal,
    }),
}
